// Pure <vf-income-explorer> logic: wire parsers and fetch plumbing for the
// drill-down endpoints (/blocks, /blocks/{slot}/txs, /tx/{signature}), the
// four-level navigation state machine, payer grouping, and the coverage /
// footnote / absence strings. No DOM, no ECharts — data in, data out.
//
// Honesty rules enforced here:
// - null means unknown/unavailable; parsing preserves it and callers render
//   it as explicit written absence — never 0, never a bare dash.
// - /blocks pagination is complete-or-error: pages are fetched until the
//   server returns an empty page (the server may clamp the requested limit,
//   so a short page is not a termination signal); hitting the page cap
//   raises instead of silently truncating an epoch.
// - Lamports stay integers until the display edge (format.ts).

import { ApiError, DEFAULT_TIMEOUT_MS } from "./api";
import { formatCount } from "./format";

// --- HTTP -------------------------------------------------------------------

export type FetchJson = (
  path: string,
  params: Record<string, number | undefined>,
  signal?: AbortSignal,
) => Promise<unknown>;

/**
 * GET-JSON client for one API origin, mirroring api.ts getJson (private to
 * that module): timeout + outer-abort wiring, {"error": ...} envelope
 * decoding, base-with-subpath preservation.
 */
export function createFetchJson(base: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): FetchJson {
  return async (path, params, signal) => {
    let url: URL;
    try {
      url = new URL(base.replace(/\/+$/, "") + path);
    } catch {
      throw new ApiError(`invalid api-base URL: "${base}"`);
    }
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) {
        url.searchParams.set(k, String(v));
      }
    }

    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    const onOuterAbort = (): void => ctl.abort();
    signal?.addEventListener("abort", onOuterAbort, { once: true });
    if (signal?.aborted) {
      ctl.abort();
    }

    try {
      let res: Response;
      try {
        res = await fetch(url, {
          signal: ctl.signal,
          headers: { accept: "application/json" },
        });
      } catch (e) {
        if (signal?.aborted) {
          throw new ApiError(`${path}: request aborted`);
        }
        if (ctl.signal.aborted) {
          throw new ApiError(`${path}: timeout after ${timeoutMs} ms`);
        }
        throw new ApiError(`${path}: network error (${e instanceof Error ? e.message : String(e)})`);
      }
      if (!res.ok) {
        let detail = "";
        try {
          const body: unknown = await res.json();
          if (typeof body === "object" && body !== null && "error" in body) {
            const msg = (body as Record<string, unknown>)["error"];
            if (typeof msg === "string") {
              detail = `: ${msg}`;
            }
          }
        } catch {
          // Non-JSON error body; the status code is the message.
        }
        throw new ApiError(`${path}: HTTP ${res.status}${detail}`, res.status);
      }
      try {
        return await res.json();
      } catch {
        throw new ApiError(`${path}: response is not valid JSON`);
      }
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onOuterAbort);
    }
  };
}

// --- wire types + parsers -----------------------------------------------------

/** Subset of a /blocks row this component renders (API rows.rs BlockRow). */
export interface ExplorerBlockRow {
  slot: number;
  epoch: number;
  blockTime: number | null;
  /** Ledger ground truth for leader fee income in this block. */
  feeRewardLamports: number;
  tipsLamports: number;
}

/** One stored transaction of /blocks/{slot}/txs (API rows.rs BlockTxRow). */
export interface BlockTxRowUi {
  txIndex: number;
  signature: string;
  payer: string;
  feeLamports: number;
  priorityFeeLamports: number;
  tipLamports: number;
  computeUnits: number | null;
  success: boolean;
  programIds: string[];
}

/** /blocks/{slot}/txs envelope (API tx.rs BlockTxsResponse). */
export interface BlockTxsData {
  slot: number;
  /** Served coverage note, verbatim ("top-10 payers"). */
  coverage: string;
  storedTxCount: number;
  nonVoteTxCount: number;
  rows: BlockTxRowUi[];
}

/** One nonzero balance delta of /tx/{signature} (API tx.rs BalanceChange). */
export interface BalanceChangeUi {
  account: string;
  preLamports: number;
  postLamports: number;
  deltaLamports: number;
}

/**
 * Spec-fixed decode served by /tx (API tx_decode.rs DecodedInstruction):
 * ComputeBudget layouts only, tagged by `type`. An unknown tag (a newer
 * daemon than this UI) or a field exceeding Number.MAX_SAFE_INTEGER parses
 * to null — precision-or-drop; the raw base58 is always served alongside.
 */
export type DecodedInstructionUi =
  | { type: "request_units_deprecated"; units: number; additionalFeeLamports: number }
  | { type: "request_heap_frame"; bytes: number }
  | { type: "set_compute_unit_limit"; units: number }
  | { type: "set_compute_unit_price"; microLamportsPerCu: number }
  | { type: "set_loaded_accounts_data_size_limit"; bytes: number };

/** Raw instruction-data facts (API tx_decode.rs RawInstructionData). */
export interface RawInstructionDataUi {
  base58: string;
  /** Decoded byte length; null when the served base58 did not decode server-side. */
  len: number | null;
  /** First 8 bytes as hex (Anchor-convention discriminator); null under 8 bytes. */
  discriminatorHex: string | null;
}

/** One instruction of /tx/{signature} (API tx.rs InstructionDetail). */
export interface InstructionUi {
  programId: string;
  label: string | null;
  parsedType: string | null;
  /**
   * The RPC node's own jsonParsed `info` passed through verbatim — an
   * object for most parsed programs, a bare string for spl-memo. Null when
   * the node did not parse the instruction (or the daemon predates the
   * field). Rendered verbatim, never interpreted.
   */
  parsedInfo: unknown;
  decoded: DecodedInstructionUi | null;
  data: RawInstructionDataUi | null;
}

/** /tx/{signature} envelope (API tx.rs TxDetailResponse). */
export interface TxDetailData {
  signature: string;
  slot: number;
  blockTime: number | null;
  success: boolean;
  feeLamports: number;
  payer: string;
  computeUnits: number | null;
  balanceChanges: BalanceChangeUi[];
  instructions: InstructionUi[];
  logMessagesTail: string[];
}

function asObject(v: unknown, ctx: string): Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new ApiError(`${ctx}: expected a JSON object`);
  }
  return v as Record<string, unknown>;
}

function asArray(v: unknown, ctx: string): unknown[] {
  if (!Array.isArray(v)) {
    throw new ApiError(`${ctx}: expected a JSON array`);
  }
  return v;
}

function reqInt(r: Record<string, unknown>, key: string, ctx: string): number {
  const v = r[key];
  if (typeof v !== "number" || !Number.isSafeInteger(v)) {
    throw new ApiError(`${ctx}: field "${key}" is not an integer`);
  }
  return v;
}

function reqStr(r: Record<string, unknown>, key: string, ctx: string): string {
  const v = r[key];
  if (typeof v !== "string") {
    throw new ApiError(`${ctx}: field "${key}" is not a string`);
  }
  return v;
}

function reqBool(r: Record<string, unknown>, key: string, ctx: string): boolean {
  const v = r[key];
  if (typeof v !== "boolean") {
    throw new ApiError(`${ctx}: field "${key}" is not a boolean`);
  }
  return v;
}

/** Preserves null/absent as null; anything non-numeric is also null. */
function intOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isSafeInteger(v) ? v : null;
}

function strArray(v: unknown, ctx: string): string[] {
  return asArray(v ?? [], ctx).map((s) => {
    if (typeof s !== "string") {
      throw new ApiError(`${ctx}: expected string array entries`);
    }
    return s;
  });
}

/** Parses one /blocks page envelope ({range, rows}) into the rendered subset. */
export function parseBlocksPage(json: unknown): ExplorerBlockRow[] {
  const ctx = "/blocks";
  const envelope = asObject(json, ctx);
  return asArray(envelope["rows"], `${ctx} rows`).map((raw, i) => {
    const r = asObject(raw, `${ctx} row ${i}`);
    const rowCtx = `${ctx} row ${i}`;
    return {
      slot: reqInt(r, "slot", rowCtx),
      epoch: reqInt(r, "epoch", rowCtx),
      blockTime: intOrNull(r["block_time"]),
      feeRewardLamports: reqInt(r, "fee_reward_lamports", rowCtx),
      tipsLamports: reqInt(r, "tips_lamports", rowCtx),
    };
  });
}

export function parseBlockTxs(json: unknown): BlockTxsData {
  const ctx = "/blocks/{slot}/txs";
  const o = asObject(json, ctx);
  const rows = asArray(o["rows"], `${ctx} rows`).map((raw, i) => {
    const r = asObject(raw, `${ctx} row ${i}`);
    const rowCtx = `${ctx} row ${i}`;
    return {
      txIndex: reqInt(r, "tx_index", rowCtx),
      signature: reqStr(r, "signature", rowCtx),
      payer: reqStr(r, "payer", rowCtx),
      feeLamports: reqInt(r, "fee_lamports", rowCtx),
      priorityFeeLamports: reqInt(r, "priority_fee_lamports", rowCtx),
      tipLamports: reqInt(r, "tip_lamports", rowCtx),
      computeUnits: intOrNull(r["compute_units"]),
      success: reqBool(r, "success", rowCtx),
      programIds: strArray(r["program_ids"], `${rowCtx} program_ids`),
    };
  });
  return {
    slot: reqInt(o, "slot", ctx),
    coverage: reqStr(o, "coverage", ctx),
    storedTxCount: reqInt(o, "stored_tx_count", ctx),
    nonVoteTxCount: reqInt(o, "non_vote_tx_count", ctx),
    rows,
  };
}

/**
 * Strict-when-known, null-when-not parse of the served `decoded` object.
 * Null (or absence — a pre-0.11.1 daemon) stays null. A known `type` with
 * any field missing or outside safe-integer range collapses to null rather
 * than rendering an imprecise or partial figure (precision-or-drop); the
 * instruction's raw base58 remains served either way, so degradation is
 * visible, never false. An unknown `type` (daemon newer than this UI) also
 * collapses to null for the same reason.
 */
export function parseDecodedInstruction(v: unknown): DecodedInstructionUi | null {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    return null;
  }
  const r = v as Record<string, unknown>;
  const num = (key: string): number | null => intOrNull(r[key]);
  switch (r["type"]) {
    case "request_units_deprecated": {
      const units = num("units");
      const additionalFeeLamports = num("additional_fee_lamports");
      return units === null || additionalFeeLamports === null
        ? null
        : { type: "request_units_deprecated", units, additionalFeeLamports };
    }
    case "request_heap_frame": {
      const bytes = num("bytes");
      return bytes === null ? null : { type: "request_heap_frame", bytes };
    }
    case "set_compute_unit_limit": {
      const units = num("units");
      return units === null ? null : { type: "set_compute_unit_limit", units };
    }
    case "set_compute_unit_price": {
      const microLamportsPerCu = num("micro_lamports_per_cu");
      return microLamportsPerCu === null
        ? null
        : { type: "set_compute_unit_price", microLamportsPerCu };
    }
    case "set_loaded_accounts_data_size_limit": {
      const bytes = num("bytes");
      return bytes === null ? null : { type: "set_loaded_accounts_data_size_limit", bytes };
    }
    default:
      return null;
  }
}

/**
 * Parses the served raw-data object. Null/absent stays null; a present
 * object must carry `base58` (a served data object without its payload is a
 * malformed response, and this file's parsers fail loudly on those).
 */
export function parseRawInstructionData(v: unknown, ctx: string): RawInstructionDataUi | null {
  if (v === null || v === undefined) {
    return null;
  }
  const r = asObject(v, ctx);
  const discriminator = r["discriminator_hex"];
  return {
    base58: reqStr(r, "base58", ctx),
    len: intOrNull(r["len"]),
    discriminatorHex: typeof discriminator === "string" ? discriminator : null,
  };
}

export function parseTxDetail(json: unknown): TxDetailData {
  const ctx = "/tx";
  const o = asObject(json, ctx);
  const balanceChanges = asArray(o["balance_changes"], `${ctx} balance_changes`).map((raw, i) => {
    const r = asObject(raw, `${ctx} balance_changes ${i}`);
    const rowCtx = `${ctx} balance_changes ${i}`;
    return {
      account: reqStr(r, "account", rowCtx),
      preLamports: reqInt(r, "pre_lamports", rowCtx),
      postLamports: reqInt(r, "post_lamports", rowCtx),
      deltaLamports: reqInt(r, "delta_lamports", rowCtx),
    };
  });
  const instructions = asArray(o["instructions"], `${ctx} instructions`).map((raw, i) => {
    const r = asObject(raw, `${ctx} instructions ${i}`);
    const label = r["label"];
    const parsedType = r["parsed_type"];
    return {
      programId: reqStr(r, "program_id", `${ctx} instructions ${i}`),
      label: typeof label === "string" ? label : null,
      parsedType: typeof parsedType === "string" ? parsedType : null,
      parsedInfo: r["parsed_info"] ?? null,
      decoded: parseDecodedInstruction(r["decoded"]),
      data: parseRawInstructionData(r["data"], `${ctx} instructions ${i} data`),
    };
  });
  return {
    signature: reqStr(o, "signature", ctx),
    slot: reqInt(o, "slot", ctx),
    blockTime: intOrNull(o["block_time"]),
    success: reqBool(o, "success", ctx),
    feeLamports: reqInt(o, "fee_lamports", ctx),
    payer: reqStr(o, "payer", ctx),
    computeUnits: intOrNull(o["compute_units"]),
    balanceChanges,
    instructions,
    logMessagesTail: strArray(o["log_messages_tail"], `${ctx} log_messages_tail`),
  };
}

// --- epoch geometry + block pagination ----------------------------------------

/** Mainnet epoch geometry: epoch E covers slots [E*432000, (E+1)*432000). */
export const EPOCH_SLOTS = 432_000;

/** Inclusive slot bounds for one epoch (the API's SLOT_BOUNDS is inclusive on both ends). */
export function epochSlotRange(epoch: number): { fromSlot: number; toSlot: number } {
  const fromSlot = epoch * EPOCH_SLOTS;
  return { fromSlot, toSlot: fromSlot + EPOCH_SLOTS - 1 };
}

/** Requested page size; the server clamps to its own max_rows (default 500). */
export const BLOCKS_PAGE_LIMIT = 500;

/** Page-loop guard: 200 x 500 = 100k blocks, beyond any real epoch's production. */
export const BLOCKS_PAGE_MAX = 200;

/**
 * Fetches EVERY produced block of one epoch via the /blocks keyset cursor
 * (`after`, exclusive, ascending by slot). Termination is an empty page —
 * never a short page, because the server may clamp the requested limit
 * below what was asked. Exceeding the page cap raises: an incomplete epoch
 * is an error, not a chart.
 */
export async function fetchEpochBlocks(
  fetchJson: FetchJson,
  epoch: number,
  signal?: AbortSignal,
): Promise<ExplorerBlockRow[]> {
  const { fromSlot, toSlot } = epochSlotRange(epoch);
  const all: ExplorerBlockRow[] = [];
  let after: number | undefined = undefined;
  for (let page = 0; page < BLOCKS_PAGE_MAX; page += 1) {
    const rows = parseBlocksPage(
      await fetchJson(
        "/blocks",
        { from_slot: fromSlot, to_slot: toSlot, limit: BLOCKS_PAGE_LIMIT, after },
        signal,
      ),
    );
    const last = rows.at(-1);
    if (last === undefined) {
      return all;
    }
    all.push(...rows);
    after = last.slot;
  }
  throw new ApiError(
    `/blocks: epoch ${epoch} pagination exceeded ${BLOCKS_PAGE_MAX} pages; refusing a possibly incomplete block set`,
  );
}

// --- navigation state machine ---------------------------------------------------

/**
 * Composed CustomEvent type for the embedded explorer's close request.
 *
 * Dispatched by the header [x] control and by a top-level Escape (ascend
 * exhausted) while embedded; the embedding host routes it to its explorer
 * toggle. One event, one mechanism — both exits share it.
 */
export const EXPLORER_CLOSE_EVENT = "vf-explorer-close";

export type ExplorerLevel =
  | { kind: "epochs" }
  | { kind: "blocks"; epoch: number }
  | { kind: "block"; epoch: number; slot: number }
  | {
      kind: "tx";
      epoch: number;
      slot: number;
      signature: string;
      /** Served tip_lamports of the level-3 row that was clicked (/tx serves no tip). */
      tipLamports: number;
    };

/** One level up; null at the top. */
export function ascend(level: ExplorerLevel): ExplorerLevel | null {
  switch (level.kind) {
    case "epochs":
      return null;
    case "blocks":
      return { kind: "epochs" };
    case "block":
      return { kind: "blocks", epoch: level.epoch };
    case "tx":
      return { kind: "block", epoch: level.epoch, slot: level.slot };
  }
}

export interface Crumb {
  label: string;
  /** Ascend target; null marks the current (rightmost) crumb. */
  target: ExplorerLevel | null;
}

/**
 * Shortened signature display form, permitted ONLY where the full signature
 * with a copy control is present in the same rendered view (the level-4
 * body); the breadcrumb is the single such surface.
 */
export function shortSignature(signature: string): string {
  return signature.length <= 11 ? signature : `${signature.slice(0, 8)}...`;
}

export function breadcrumbs(level: ExplorerLevel): Crumb[] {
  const crumbs: Crumb[] = [
    { label: "epochs", target: level.kind === "epochs" ? null : { kind: "epochs" } },
  ];
  if (level.kind === "epochs") {
    return crumbs;
  }
  crumbs.push({
    label: `epoch ${level.epoch}`,
    target: level.kind === "blocks" ? null : { kind: "blocks", epoch: level.epoch },
  });
  if (level.kind === "blocks") {
    return crumbs;
  }
  crumbs.push({
    label: `slot ${formatCount(level.slot)}`,
    target: level.kind === "block" ? null : { kind: "block", epoch: level.epoch, slot: level.slot },
  });
  if (level.kind === "block") {
    return crumbs;
  }
  crumbs.push({ label: `tx ${shortSignature(level.signature)}`, target: null });
  return crumbs;
}

// --- payer grouping ---------------------------------------------------------------

export interface PayerGroup {
  payer: string;
  txCount: number;
  /** sum of fee_lamports + tip_lamports over this payer's stored rows (labeled derivation). */
  feesPlusTipsLamports: number;
  /** This payer's rows in served order (fee+tips descending from the API). */
  rows: BlockTxRowUi[];
}

/** Groups stored rows by payer, ordered by summed fees+tips descending (tie: payer ascending). */
export function groupByPayer(rows: readonly BlockTxRowUi[]): PayerGroup[] {
  const byPayer = new Map<string, PayerGroup>();
  for (const row of rows) {
    const contribution = row.feeLamports + row.tipLamports;
    const g = byPayer.get(row.payer);
    if (g === undefined) {
      byPayer.set(row.payer, {
        payer: row.payer,
        txCount: 1,
        feesPlusTipsLamports: contribution,
        rows: [row],
      });
    } else {
      g.txCount += 1;
      g.feesPlusTipsLamports += contribution;
      g.rows.push(row);
    }
  }
  return [...byPayer.values()].sort((a, b) => {
    if (b.feesPlusTipsLamports !== a.feesPlusTipsLamports) {
      return b.feesPlusTipsLamports - a.feesPlusTipsLamports;
    }
    return a.payer < b.payer ? -1 : a.payer > b.payer ? 1 : 0;
  });
}

// --- rendered copy (single source for the element and its tests) --------------------

/** Level-3 coverage statement: the served coverage note plus the stored/non-vote denominators. */
export function coverageLine(coverage: string, storedTxCount: number, nonVoteTxCount: number): string {
  return `${coverage} · ${formatCount(storedTxCount)} of ${formatCount(nonVoteTxCount)} non-vote transactions stored`;
}

export const EPOCHS_FOOTNOTE =
  "What the validator made in an epoch = block fees + MEV claimed + BAM boost - vote cost - operator tx cost " +
  "(the ledger net_total) - shown in each epoch tooltip's header and derived line by line in its body. " +
  "Tips are not validator income: they flow to stakers-side tip accounts, and the validator's share arrives as MEV claimed. " +
  "Fees and tips accrue per block; MEV claims and BAM boost settle per epoch. " +
  "BAM boost is the SOL valuation served by the API (bam_sol_lamports).";

/** Level-2 header line: "epoch 1000 · 1,234 blocks produced". */
export function blocksHeaderLine(epoch: number, blockCount: number): string {
  const noun = blockCount === 1 ? "block" : "blocks";
  return `epoch ${epoch} · ${formatCount(blockCount)} ${noun} produced`;
}

/**
 * Decoded-instruction facts in words with exact counts. Values are the
 * served figures verbatim — no arithmetic (the priority fee is already a
 * served figure elsewhere; price and limit are never multiplied here).
 */
export function decodedInstructionText(d: DecodedInstructionUi): string {
  switch (d.type) {
    case "request_units_deprecated":
      return (
        `request units (deprecated) · ${formatCount(d.units)} CU · ` +
        `additional fee ${formatCount(d.additionalFeeLamports)} lamports`
      );
    case "request_heap_frame":
      return `request heap frame · ${formatCount(d.bytes)} bytes`;
    case "set_compute_unit_limit":
      return `set compute unit limit · ${formatCount(d.units)} CU`;
    case "set_compute_unit_price":
      return `set compute unit price · ${formatCount(d.microLamportsPerCu)} micro-lamports per CU`;
    case "set_loaded_accounts_data_size_limit":
      return `set loaded accounts data size limit · ${formatCount(d.bytes)} bytes`;
  }
}

/**
 * Raw-data facts line: byte length, plus the 8-byte discriminator when the
 * payload is long enough. `len` null means the served base58 did not decode
 * server-side — stated, not hidden.
 */
export function rawDataFactsText(d: RawInstructionDataUi): string {
  const length = d.len === null ? "byte length not decoded" : `${formatCount(d.len)} bytes`;
  return d.discriminatorHex === null ? length : `${length} · discriminator ${d.discriminatorHex}`;
}

export const NOT_SERVED_TEXT = "not served";
export const NO_INCOME_TEXT = "no income rows served for the selected epoch range";
export const NO_STORED_TXS_TEXT = "no transactions stored for this block";
export const NO_BALANCE_CHANGES_TEXT = "no accounts with a nonzero balance change";
export const NO_LOGS_TEXT = "no log messages returned";
export const BLOCK_TIME_NOT_SERVED_TEXT = "block time not served";
export const NO_REGISTRY_LABEL_TEXT = "no registry label";

export function noBlocksText(epoch: number): string {
  return `no produced blocks recorded for epoch ${epoch}`;
}

/**
 * Lamports -> SOL as a plain number, for ECharts plot geometry only.
 * Every visible figure (tooltip, cell) renders through the exact BigInt
 * formatter (format.ts lamportsToSol) from the integer instead.
 */
export function solNumber(lamports: number): number {
  return lamports / 1_000_000_000;
}
